/**
 * workdayApi.js
 *
 * Data layer of the "Munkanap" (workday clock-in / clock-out) feature. Talks to `/api/workday/*`.
 *
 * FAIL-SOFT by contract: the backend may not be deployed yet (404 / 405 / 501). Reads never throw — they resolve to
 * `{ok:false, unsupported}` so the UI hides the feature instead of crashing. Writes throw an Error carrying
 * `status` / `code` (e.g. 409 `WORKDAY_NOT_RUNNING`, 422 `INVALID_SNOOZE`) so the caller can tell the user why;
 * `unsupported` writes carry `error.unsupported = true`.
 *
 * @module helpers/workdayApi
 */

import {apiFetch} from './WorkSheetContext';
import {DEFAULT_TARGET_MINUTES, SNOOZE_OPTIONS, dayKeyOf, isValidSnooze} from './workdayRules';

const BASE = '/workday';

/**
 * @typedef {object} WorkdaySession
 * @property {string} id
 * @property {string} dayKey
 * @property {string|null} startedAt ISO.
 * @property {string|null} endedAt ISO.
 * @property {'running'|'ended'|'autoClosed'} status
 * @property {number} targetMinutes
 * @property {string|null} reminderAt ISO.
 * @property {number} snoozeCount
 * @property {string} startSource
 * @property {string} endSource
 * @property {string} note
 * @property {boolean} paused a break is open (the session stays `running`).
 * @property {string|null} pausedAt ISO start of the open break.
 * @property {number} breakSec closed break seconds.
 */

/**
 * @typedef {object} WorkdayState
 * @property {WorkdaySession|null} running
 * @property {number} elapsedSec seconds at `serverNow`.
 * @property {{dayKey: string, workedSec: number, sessions: WorkdaySession[]}} today
 * @property {boolean} reminderDue
 * @property {{targetMinutes: number, snoozeOptions: number[]}} settings
 * @property {{running: boolean, projectId: string|null, taskId: string|null, start: string|null, note: string}|null} timer
 * @property {string|null} serverNow ISO.
 * @property {number} fetchedAt client epoch ms when this state was received (skew anchor).
 * @property {boolean} [alreadyRunning]
 * @property {object|null} [stoppedTimer]
 */

/** HTTP statuses that mean "this backend does not know the feature (yet)". */
const UNSUPPORTED_STATUSES = [404, 405, 501];

/** Where the workday is worked (= the HR timesheet `worked` type the backend mirrors). */
export const WORK_MODES = Object.freeze(['OFFICE', 'HOME_OFFICE']);

/**
 * Whether an error means the endpoint is not deployed.
 * @param {*} error thrown error.
 * @returns {boolean} true when unsupported.
 */
export const isUnsupportedError = (error) => {
    const status = Number(error?.status ?? error?.statusCode ?? error?.response?.status);

    return UNSUPPORTED_STATUSES.includes(status);
};

const str = (v) => (v === null || v === undefined ? '' : String(v));
const iso = (v) => (v ? str(v) : null);

/**
 * Normalize one session.
 * @param {object} raw raw session. @returns {WorkdaySession|null} session, or null for junk.
 */
export const normalizeSession = (raw) => {
    if (!raw || typeof raw !== 'object') {
        return null;
    }

    const status = ['running', 'ended', 'autoClosed'].includes(raw.status) ? raw.status : (raw.endedAt ? 'ended' : 'running');

    return {
        id: str(raw._id || raw.id),
        dayKey: str(raw.dayKey),
        startedAt: iso(raw.startedAt),
        endedAt: iso(raw.endedAt),
        status,
        targetMinutes: Number(raw.targetMinutes) > 0 ? Number(raw.targetMinutes) : DEFAULT_TARGET_MINUTES,
        reminderAt: iso(raw.reminderAt),
        snoozeCount: Math.max(0, Number(raw.snoozeCount) || 0),
        startSource: str(raw.startSource),
        endSource: str(raw.endSource),
        note: str(raw.note),
        paused: raw.paused === true || !!raw.pausedAt,
        pausedAt: iso(raw.pausedAt),
        breakSec: Math.max(0, Math.floor(Number(raw.breakSec) || 0)),
        workMode: WORK_MODES.includes(raw.workMode) ? raw.workMode : 'OFFICE',
        workModeSource: ['gps', 'manual', 'default'].includes(raw.workModeSource) ? raw.workModeSource : 'default',
        workModePlaceName: raw.workModePlaceName ? str(raw.workModePlaceName) : null,
    };
};

/**
 * Normalize a `WorkdayState` payload (the `data` of the envelope, or a realtime push) and stamp the client receive time.
 * @param {object|null|undefined} raw raw state.
 * @param {number} [fetchedAt] client epoch ms.
 * @returns {WorkdayState|null} state, or null when the payload is not a state.
 */
export const normalizeWorkdayState = (raw, fetchedAt = Date.now()) => {
    if (!raw || typeof raw !== 'object') {
        return null;
    }

    const running = normalizeSession(raw.running);
    const sessions = (Array.isArray(raw.today?.sessions) ? raw.today.sessions : []).map(normalizeSession).filter(Boolean);
    const options = Array.isArray(raw.settings?.snoozeOptions)
        ? raw.settings.snoozeOptions.map(Number).filter(isValidSnooze)
        : [];
    const timer = raw.timer && typeof raw.timer === 'object'
        ? {
            running: raw.timer.running === true,
            projectId: raw.timer.projectId ? str(raw.timer.projectId) : null,
            taskId: raw.timer.taskId ? str(raw.timer.taskId) : null,
            start: iso(raw.timer.start),
            note: str(raw.timer.note),
        }
        : null;

    return {
        running: running && running.status === 'running' ? running : null,
        elapsedSec: Number.isFinite(Number(raw.elapsedSec)) ? Math.max(0, Number(raw.elapsedSec)) : 0,
        paused: !!running && running.status === 'running' && (raw.paused === true || running.paused),
        breakSec: Math.max(0, Math.floor(Number(raw.breakSec) || (running ? running.breakSec : 0) || 0)),
        today: {
            dayKey: str(raw.today?.dayKey) || dayKeyOf(),
            workedSec: Math.max(0, Number(raw.today?.workedSec) || 0),
            sessions,
        },
        reminderDue: raw.reminderDue === true,
        settings: {
            targetMinutes: Number(raw.settings?.targetMinutes) > 0 ? Number(raw.settings.targetMinutes) : DEFAULT_TARGET_MINUTES,
            snoozeOptions: options.length ? options : [...SNOOZE_OPTIONS],
        },
        timer,
        serverNow: iso(raw.serverNow),
        fetchedAt,
        alreadyRunning: raw.alreadyRunning === true,
        alreadyPaused: raw.alreadyPaused === true,
        stoppedTimer: raw.stoppedTimer || null,
    };
};

/**
 * Normalize the additive `/widgets/summary` `workday` field.
 * @param {object|null|undefined} raw raw field. @returns {object|null} compact workday summary, or null.
 */
export const normalizeWidgetWorkday = (raw) => {
    if (!raw || typeof raw !== 'object') {
        return null;
    }

    const running = raw.running === true;
    const paused = running && (raw.status === 'paused' || (raw.status !== 'running' && raw.paused === true));
    const elapsedSec = Math.max(0, Number(raw.elapsedSec) || 0);

    return {
        running,
        // Explicit clock state (idle | running | paused); an older backend only sends running/paused.
        status: running ? (paused ? 'paused' : 'running') : 'idle',
        paused,
        pausedAt: iso(raw.pausedAt),
        breakSec: Math.max(0, Math.floor(Number(raw.breakSec) || 0)),
        startedAt: iso(raw.startedAt),
        elapsedSec,
        // Worked seconds (breaks excluded) at the server's `generatedAt`; falls back to `elapsedSec` for an older backend.
        workedSecAtSnapshot: Math.max(0, Math.floor(Number(raw.workedSecAtSnapshot ?? elapsedSec) || 0)),
        reminderAt: iso(raw.reminderAt),
        reminderDue: raw.reminderDue === true,
        targetMinutes: Number(raw.targetMinutes) > 0 ? Number(raw.targetMinutes) : DEFAULT_TARGET_MINUTES,
    };
};

const unwrap = (res) => (res && typeof res === 'object' && 'data' in res && res.data && typeof res.data === 'object' ? res.data : res);

const call = async (segment, body) => {
    const res = await apiFetch(`${BASE}/${segment}`, {method: 'POST', body: JSON.stringify(body)});
    const state = normalizeWorkdayState(unwrap(res));

    if (!state) {
        throw Object.assign(new Error('Empty workday response'), {status: 502});
    }

    return state;
};

const rethrow = (error) => {
    if (isUnsupportedError(error)) {
        error.unsupported = true;
    }

    throw error;
};

/**
 * Read the caller's current workday state. Never throws.
 * @param {{dayKey?: string}} [opts] day to read (local day key).
 * @returns {Promise<{ok: boolean, unsupported: boolean, state: WorkdayState|null}>} result.
 */
export const fetchWorkdayState = async ({dayKey} = {}) => {
    try {
        const res = await apiFetch(`${BASE}/current?dayKey=${encodeURIComponent(dayKey || dayKeyOf())}`, {skipDedup: true, skipCache: true});
        const state = normalizeWorkdayState(unwrap(res));

        return state ? {ok: true, unsupported: false, state} : {ok: false, unsupported: false, state: null};
    } catch (error) {
        return {ok: false, unsupported: isUnsupportedError(error), state: null};
    }
};

/**
 * Start the workday (idempotent: `alreadyRunning` on the state when one runs).
 * The server decides office / home office from `location` (store place fences); `workMode` is only the fallback
 * when there is no usable fix.
 * @param {{note?: string, source?: string, workMode?: 'OFFICE'|'HOME_OFFICE', location?: {lat: number, lng: number, accuracy?: number}}} [opts] options (`location` = work-event fix, recorded server-side).
 * @returns {Promise<WorkdayState>} state. @throws {Error} with `status` / `code` / `unsupported`.
 */
export const startWorkday = async ({note = '', source = 'app', workMode = null, location = null} = {}) => {
    try {
        return await call('start', {
            source,
            note,
            dayKey: dayKeyOf(),
            tzOffsetMinutes: -new Date().getTimezoneOffset(),
            ...(WORK_MODES.includes(workMode) ? {workMode} : {}),
            ...(location ? {location} : {}),
        });
    } catch (error) {
        return rethrow(error);
    }
};

/**
 * Switch the running workday between office and home office (manual; refused with 409 `WORK_MODE_GPS_LOCKED` when GPS
 * decided it).
 * @param {'OFFICE'|'HOME_OFFICE'} workMode mode. @returns {Promise<WorkdayState>} state. @throws {Error} 409 / 422.
 */
export const setWorkdayMode = async (workMode) => {
    if (!WORK_MODES.includes(workMode)) {
        throw Object.assign(new Error('Invalid work mode'), {status: 422, code: 'INVALID_WORK_MODE'});
    }

    try {
        return await call('mode', {workMode});
    } catch (error) {
        return rethrow(error);
    }
};

/**
 * End the workday.
 * @param {{stopTimers?: boolean, note?: string, source?: string, location?: {lat: number, lng: number, accuracy?: number}}} [opts] options.
 * @returns {Promise<WorkdayState>} state (with `stoppedTimer`). @throws {Error} 409 `WORKDAY_NOT_RUNNING` etc.
 */
export const stopWorkday = async ({stopTimers = false, note = '', source = 'app', location = null} = {}) => {
    try {
        return await call('stop', {source, stopTimers: !!stopTimers, note, ...(location ? {location} : {})});
    } catch (error) {
        return rethrow(error);
    }
};

/**
 * Pause the workday (open a break). Idempotent server-side (`alreadyPaused`). Never asks for GPS.
 * @param {{source?: string}} [opts] options.
 * @returns {Promise<WorkdayState>} state. @throws {Error} 409 `WORKDAY_NOT_RUNNING`.
 */
export const pauseWorkday = async ({source = 'app'} = {}) => {
    try {
        return await call('pause', {source});
    } catch (error) {
        return rethrow(error);
    }
};

/**
 * Resume the workday (close the break). Idempotent server-side (`alreadyRunning`).
 * @param {{source?: string}} [opts] options.
 * @returns {Promise<WorkdayState>} state. @throws {Error} 409 `WORKDAY_NOT_RUNNING`.
 */
export const resumeWorkday = async ({source = 'app'} = {}) => {
    try {
        return await call('resume', {source});
    } catch (error) {
        return rethrow(error);
    }
};

/**
 * Postpone the end-of-day reminder.
 * @param {number} minutes 15 | 30 | 60. @returns {Promise<WorkdayState>} state. @throws {Error} 422 `INVALID_SNOOZE`.
 */
export const snoozeWorkday = async (minutes) => {
    if (!isValidSnooze(minutes)) {
        throw Object.assign(new Error('Invalid snooze'), {status: 422, code: 'INVALID_SNOOZE'});
    }

    try {
        return await call('snooze', {minutes: Number(minutes)});
    } catch (error) {
        return rethrow(error);
    }
};
